import { Accept, FileRejection, useDropzone } from "react-dropzone";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React, { useCallback, useEffect, useState } from "react";

import _ from "lodash";
import axios from "axios";
import clsx from "clsx";
import config from "config";

axios.defaults.baseURL = config.asset.uri;

interface ImageUploadAreaProps {
  id: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number;
  minSize?: number;
  accept?: Accept;
  disabled?: boolean;
  label?: string;
  values: any;
  setFieldValue: any;
  setFieldError: any;
  setFieldTouched: any;
  handleBlur: any;
  errors?: any;
  touched?: any;
  className?: string;
  required?: boolean;
  description?: string;
}

interface Upload extends File {
  path?: string;
  preview?: string;
}

const ImageUploadArea: React.FC<ImageUploadAreaProps> = ({
  multiple = false,
  maxFiles = 1,
  maxSize = 1024 * 1024 * 5,
  minSize = 1,
  accept = { "image/*": [".png", ".jpeg", ".jpg"] },
  errors,
  values,
  setFieldValue,
  setFieldError,
  setFieldTouched,
  touched,
  id,
  label,
  className = "",
  required,
  description
}) => {
  const [files, setFiles] = useState<Upload[]>([]);
  const [loading, setLoading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setFieldTouched(id, true, true);

      if (!acceptedFiles.length) {
        setFieldError(
          id,
          _.chain(rejectedFiles)
            .map("errors")
            .flatten()
            .map("message")
            .join(", ")
            .value()
        );
        return;
      }

      const newFiles = acceptedFiles.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      );

      if (multiple) {
        setFiles((prev) => [...prev, ...newFiles]);
      } else {
        setFiles(newFiles);
      }

      setLoading(true);

      // Upload files
      if (multiple) {
        const urls: string[] = [];
        const data = new FormData();
        acceptedFiles.forEach((file, index) => {
          data.append(`assets[${index}]`, file);
          // data.append("offer_id", _.uniqueId());
        });
        try {
          const res = await axios.post("/api/upload-promotion-assets", data, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          console.log("File uploaded:", res.data.paths);
          urls.push(...res.data.paths);
        } catch (err: any) {
          setFieldError(id, `Error uploading files: ${err.message}`);
        }
        setFieldValue(id, [...(_.get(values, id) || []), ...urls]);
      } else {
        const file = acceptedFiles[0];
        const data = new FormData();
        data.append("assets[0]", file);
        try {
          const res = await axios.post("/attachFilesToOffer", data, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setFieldValue(id, res.data);
        } catch (err) { }
      }

      setLoading(false);
    },
    [multiple, id, values]
  );

  const removeFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);

    if (multiple) {
      const newUrls = [...(_.get(values, id) || [])];
      newUrls.splice(index, 1);
      setFieldValue(id, newUrls);
    } else {
      setFieldValue(id, null);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles,
    multiple,
    accept,
    maxSize,
    minSize,
    disabled: loading,
  });

  useEffect(() => {
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview!));
  }, [files]);

  const renderPreview = (file: any, index: number) => {
    const isPdf =
      file?.path?.endsWith(".pdf") || file?.preview?.endsWith(".pdf");
    return (
      <div
        key={index}
        className="relative w-24 h-24 border rounded-md overflow-hidden shadow-sm bg-white"
      >
        {isPdf ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-xs">
            PDF
          </div>
        ) : (
          <img
            src={file.preview || file}
            alt=""
            className="object-cover w-full h-full"
          />
        )}
        <button
          type="button"
          onClick={() => removeFile(index)}
          className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 hover:bg-black/70"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {description && (
        <p className="text-xs text-gray-500">{description}</p>
      )}
      <div
        {...getRootProps()}
        className={clsx(
          "border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition",
          isDragActive && "bg-primary-50 border-primary-400",
          className
        )}
      >
        <input {...getInputProps()} />
        <PlusIcon className="h-8 w-8 text-gray-400 mb-1" />
        <p className="text-sm text-gray-600">
          {isDragActive ? "Drop files here" : "Click or drag files to upload"}
        </p>
        {loading && <p className="text-xs text-amber-500 mt-1">Uploading...</p>}
      </div>

      {multiple ? (
        <div className="flex flex-wrap gap-3">
          {/* {files.map(renderPreview)} */}
          {_.get(values, id, []).map((url: string, index: number) =>
            renderPreview(url, index)
          )}
        </div>
      ) : files.length || _.get(values, id) ? (
        <div className="w-48">
          {renderPreview(files[0] || _.get(values, id), 0)}
        </div>
      ) : null}

      {_.get(errors, id) && _.get(touched, id) && (
        <p className="text-sm text-red-600">{_.get(errors, id)}</p>
      )}
    </div>
  );
};

export default ImageUploadArea;
